import * as monthUtils from '@actual-app/core/shared/months';
import type { IntegerAmount } from '@actual-app/core/shared/util';

/**
 * Cuentas de la botella mientras navega (ficha de la categoría): dónde va,
 * qué boyas ha pasado, a qué ritmo llega y lo que toca apartar este mes.
 * Todo informativo: nunca «vas con retraso».
 */

/** Boyas de la travesía (un cuarto, media, tres cuartos). */
export const BOYAS = [0.25, 0.5, 0.75] as const;

/** Avance 0..1 (saldo sobre meta). */
export function avance(saldo: IntegerAmount, meta: IntegerAmount): number {
  if (!(meta > 0)) {
    return 0;
  }
  return Math.max(0, Math.min(1, saldo / meta));
}

/** Boyas ya pasadas. */
export function boyasPasadas(progreso: number): number {
  return BOYAS.filter(b => progreso >= b).length;
}

/**
 * Posición horizontal de la botella en la escena de la ficha: del
 * horizonte (derecha, 0 %) al muelle (izquierda, 100 %).
 */
export function posicionBotella(
  progreso: number,
  desde: number,
  hasta: number,
): number {
  const p = Math.max(0, Math.min(1, progreso));
  return +(desde + (hasta - desde) * p).toFixed(1);
}

/** Meses que se miran para el ritmo (los últimos con dato). */
export const MESES_RITMO = 3;

/**
 * Mes estimado de llegada (`AAAA-MM`) al ritmo de lo apartado en los
 * últimos meses (media de `MESES_RITMO`, lo negativo cuenta 0). null si ya
 * llegó o si no se aparta nada (no hay ritmo que extrapolar).
 */
export function mesEstimado({
  saldo,
  meta,
  asignados,
  mes,
}: {
  saldo: IntegerAmount;
  meta: IntegerAmount;
  /** Lo asignado en cada mes, del más antiguo al actual (incluido). */
  asignados: readonly IntegerAmount[];
  /** Mes actual `AAAA-MM`. */
  mes: string;
}): string | null {
  const falta = meta - saldo;
  if (falta <= 0) {
    return null;
  }
  const ultimos = asignados.slice(-MESES_RITMO);
  if (ultimos.length === 0) {
    return null;
  }
  const media =
    ultimos.reduce((s, v) => s + Math.max(0, v), 0) / ultimos.length;
  if (!(media > 0)) {
    return null;
  }
  // Lo del mes actual ya está en el saldo: la siguiente aportación es el
  // mes que viene.
  return monthUtils.addMonths(mes, Math.ceil(falta / media));
}

/**
 * Lo que toca apartar este mes según la cuota de la meta (lo que pide la
 * plantilla) menos lo ya asignado. 0 si no falta nada.
 */
export function porApartar(
  cuota: IntegerAmount | null | undefined,
  asignado: IntegerAmount,
): IntegerAmount {
  return Math.max(0, (cuota ?? 0) - asignado);
}
